import { createFetcher, type FetcherConfig } from "./fetcher";
import type { Locale } from "@rr/utils";
import { createQuotesRepository } from "./quotes";
import { createOrdersRepository } from "./orders";
import { createReferencesRepository } from "./references/repository";
import { createGoodsRepository } from "./goods/repository";
import { createClientsRepository } from "./clients";
import { createDriversRepository } from "./drivers";
import { createVehiclesRepository } from "./vehicles";
import { createCarriersRepository } from "./carriers";
import { createTenantRepository } from "./tenant";
import { createCalculationsRepository } from "./calculations/repository";
import { createPlatformRepository } from "./platform/repository";
import { createFilterTemplatesRepository } from "./filter-templates";
import { createDocumentsRepository } from "./documents";
import { createInvoicesRepository } from "./invoices";
import { createPaymentsRepository } from "./payments";
import { createFinanceRepository } from "./finance";
import { createChatInboxRepository } from "./chat-inbox";
import { createSessionsRepository } from "./sessions";
import { createAssistantRepository } from "./assistant";

export type RrApiClientConfig = {
  baseUrl: string;
  token?: string;
  getToken?: FetcherConfig["getToken"];
  onUnauthorized?: FetcherConfig["onUnauthorized"];
  /**
   * Active locale. Forwarded as the `X-Language` request header by the
   * fetcher so the backend can resolve localised reference data
   * server-side. Repositories themselves are locale-agnostic.
   */
  locale: Locale;
};

/**
 * Scalable API client factory.
 *
 * The backend resolves localisation per the `X-Language` header (set by the
 * fetcher), so each repository factory is wired with just the fetcher — the
 * legacy `locale` second argument is gone.
 */
export function createRrApiClient({ baseUrl, token, getToken, onUnauthorized, locale }: RrApiClientConfig) {
  const fetcher = createFetcher({ baseUrl, token, getToken, onUnauthorized, locale });

  return {
    quotes: createQuotesRepository(fetcher),
    orders: createOrdersRepository(fetcher),
    references: createReferencesRepository(fetcher),
    goods: createGoodsRepository(fetcher),
    clients: createClientsRepository(fetcher),
    drivers: createDriversRepository(fetcher),
    vehicles: createVehiclesRepository(fetcher),
    carriers: createCarriersRepository(fetcher),
    tenant: createTenantRepository(fetcher),
    calculations: createCalculationsRepository(fetcher),
    platform: createPlatformRepository(fetcher),
    filterTemplates: createFilterTemplatesRepository(fetcher),
    documents: createDocumentsRepository(fetcher),
    invoices: createInvoicesRepository(fetcher),
    payments: createPaymentsRepository(fetcher),
    finance: createFinanceRepository(fetcher),
    chatInbox: createChatInboxRepository(fetcher),
    sessions: createSessionsRepository(fetcher),
    assistant: createAssistantRepository(fetcher),
    fetcher,
  };
}

export type RrApiClient = ReturnType<typeof createRrApiClient>;
